import { useQuery } from '@tanstack/react-query';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';
import { API_ONEPLATFORM_PODS_ASSIGNMENTS } from 'services/dataService/resources/oneplatform/constants';
import useUser from 'utils/useUser';

export interface IClinicDetail {
  guid: string;
  name: string;
  state?: string | null;
}

export interface IPodAssignment {
  guid: string;
  podName: string;
  podType: 'RPM' | 'CCM';
  priority: 'primary' | 'secondary' | 'tertiary' | 'outside';
  clinics?: IClinicDetail[];
}

export interface IPodAssignmentsResponse {
  pods: IPodAssignment[];
}

const POD_ASSIGNMENTS_STALE_TIME = 5 * 60 * 1000; // 5 minutes

const usePodAssignments = () => {
  const { hasRpmPodAssignment } = useUser();

  return useQuery<IPodAssignmentsResponse>({
    queryKey: ['pod-assignments', 'RPM'],
    queryFn: async () => {
      const { data, error } =
        await dataService.getList<IPodAssignmentsResponse>(
          apiUrlGenerator(API_ONEPLATFORM_PODS_ASSIGNMENTS, {
            podType: 'RPM',
            includeClinics: true,
          }),
        );

      if (error) {
        return Promise.reject(error);
      }

      return {
        // Cast is safe: BE guarantees `priority` is one of the four known values
        // (primary | secondary | tertiary | outside) for RPM pods with status=active.
        pods: (data?.pods ?? []) as IPodAssignment[],
      };
    },
    staleTime: POD_ASSIGNMENTS_STALE_TIME,
    enabled: hasRpmPodAssignment,
  });
};

export default usePodAssignments;
